import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { SearchFilterItemComponent, StyledButton, StyledFilterDesktop } from "@components";
import {
    setAccommodation,
    setActivity,
    setSupplier,
    setTransport,
    setDestinations,
    setStartLocations,
    setDay,
    resetSearch,
    setCurrentPage,
    useAppSelector,
    selectSearchCategories,
    setLengthOfTrip,
    setLengthOfTripCategory,
    setName,
    AppDispatch,
    setCountries,
    setStates,
    setTowns,
    setRegions,
    fetchSearchFilters,
} from "@redux";
import { categoriesApi } from "@api";
import { IOption } from "@interfaces";
import { PATH_SEARCH_RESULT } from "@routes";
import { breakpoint, theme, useWindowDimensions } from "@utilities";
import { filterConstants } from "@constants";
import isEqual from "lodash/isEqual";

interface IProps {
    onToggleShowFilter?: () => void;
    onApplyFilter?: () => void;
    onResetSearch?: () => void;
    isFilterOpen?: boolean;
    isDesktopSearchResult?: boolean;
}

export const FilterModule = (props: IProps) => {
    const { ACCOMMODATION, ACTIVITIES, TRANSPORTS, SUPPLIERS, LENGTH_OF_TRIP, DESTINATIONS, START_LOCATIONS } = filterConstants;
    const filterConstantsArr = [ACCOMMODATION, ACTIVITIES, SUPPLIERS, TRANSPORTS, DESTINATIONS, START_LOCATIONS, LENGTH_OF_TRIP];
    const [currentOptionIndex, setCurrentOptionIndex] = useState(-1);
    const searchCategories = useAppSelector(selectSearchCategories);
    const dispatch = useDispatch<AppDispatch>();
    const [lengthOfTrip, setLengthOfTripState] = useState<number | null | string | undefined>(null);
    const isSearchResultPage = window.location.pathname.includes(PATH_SEARCH_RESULT);
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const selectedLocations = useAppSelector((state) => state.locationSearch.selectedLocations);
    const filterTagModel = [
        {
            icon: "/images/filter/fi_home.png",
            title: "Accommodation Types",
            name: "accommodations",
        },
        {
            icon: "/images/filter/fi_activity.png",
            title: "Activity Types",
            name: "activities",
        },
        {
            icon: "/images/filter/fi_box.png",
            title: "Suppliers",
            name: "suppliers",
        },
        {
            icon: "/images/filter/fi_truck.png",
            title: "Transport Types",
            name: "transports",
        },
        {
            icon: "/images/filter/fi_home.png",
            title: "Destination",
            name: "destinations",
        },
        {
            icon: "/images/filter/fi_activity.png",
            title: "Start Location",
            name: "startLocations",
        },
        {
            icon: "/images/filter/fi_calendar.png",
            title: "Length of trip (days)",
            name: "duration",
        },
    ];
    const categoryMap = categoriesApi.getCategoryMap(searchCategories);
    const filtersLoaded = useAppSelector((state) => (state.searchCategories as any).filtersLoaded);
    const filtersLoading = useAppSelector((state) => (state.searchCategories as any).filtersLoading);

    useEffect(() => {
        if (!filtersLoaded && !filtersLoading) {
            dispatch(fetchSearchFilters());
        }
    }, [dispatch, filtersLoaded, filtersLoading]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const isDestinationExplore = urlParams.get("destinationExplore") === "true";

        if (selectedLocations && !isDestinationExplore) {
            dispatch(setCountries(selectedLocations.country));
            dispatch(setStates(selectedLocations.state));
            dispatch(setRegions(selectedLocations.region));
            dispatch(setTowns(selectedLocations.locality));
            dispatch(setCurrentPage(1));
            props.onApplyFilter?.();
        }
    }, [selectedLocations]);

    const handleClickTag = (categoryIndex: number) => {
        if (categoryIndex === 6) {
            if (!searchCategories.categories.duration?.length) {
                const lengthOfTrip1: IOption[] = [];
                for (let i = 1; i <= 100; ++i) {
                    lengthOfTrip1.push({ id: i, text: i.toString() });
                }
                dispatch(setLengthOfTripCategory(lengthOfTrip1));
            }
        }

        if (currentOptionIndex === categoryIndex) {
            setCurrentOptionIndex(-1);
        } else {
            setCurrentOptionIndex(categoryIndex);
        }
    };

    const handleCloseOptions = () => {
        setCurrentOptionIndex(-1);
    };

    const handleClickItem = (categoryItem: IOption) => {
        if (currentOptionIndex === 0) {
            const selectedAccommodation = searchCategories.accommodation ?? [];
            const isSelected = selectedAccommodation.some((item) => item.id === categoryItem.id);

            const updatedAccommodation = isSelected
                ? selectedAccommodation.filter((item) => item.id !== categoryItem.id)
                : [...selectedAccommodation, { id: categoryItem.id, text: categoryItem.text }];

            if (!isEqual(selectedAccommodation, updatedAccommodation)) {
                dispatch(setAccommodation(updatedAccommodation));
            }
        } else if (currentOptionIndex === 1) {
            const selectedActivities = searchCategories.accommodation ?? [];
            const isSelected = selectedActivities.some((item) => item.id === categoryItem.id);

            const updatedActivities = isSelected
                ? selectedActivities.filter((item) => item.id !== categoryItem.id)
                : [...selectedActivities, { id: categoryItem.id, text: categoryItem.text }];

            if (!isEqual(selectedActivities, updatedActivities)) {
                dispatch(setActivity(updatedActivities));
            }
        } else if (currentOptionIndex === 2) {
            const selectedSuppliers = searchCategories.suppliers ?? [];
            const isSelected = selectedSuppliers.some((item) => item.id === categoryItem.id);

            const updatedSuppliers = isSelected
                ? selectedSuppliers.filter((item) => item.id !== categoryItem.id)
                : [...selectedSuppliers, { id: categoryItem.id, text: categoryItem.text }];

            if (!isEqual(selectedSuppliers, updatedSuppliers)) {
                dispatch(setSupplier(updatedSuppliers));
            }
        } else if (currentOptionIndex === 3) {
            const transports = searchCategories.transports ?? [];
            const isSelected = transports.some((t: IOption) => t.id === categoryItem.id);

            const updatedTransports = isSelected
                ? transports.filter((t: IOption) => t.id !== categoryItem.id)
                : [...transports, { id: categoryItem.id, text: categoryItem.text }];

            if (!isEqual(transports, updatedTransports)) {
                dispatch(setTransport(updatedTransports));
            }
        } else if (currentOptionIndex === 4) {
            const selectedDest = searchCategories.destinations ?? [];
            const isSelected = selectedDest.some((item) => item.id === categoryItem.id);
            const updated = isSelected
                ? selectedDest.filter((item) => item.id !== categoryItem.id)
                : [...selectedDest, { id: categoryItem.id, text: categoryItem.text }];
            if (!isEqual(selectedDest, updated)) {
                dispatch(setDestinations(updated));
            }
        } else if (currentOptionIndex === 5) {
            const selectedLoc = searchCategories.startLocations ?? [];
            const isSelected = selectedLoc.some((item) => item.id === categoryItem.id);
            const updated = isSelected
                ? selectedLoc.filter((item) => item.id !== categoryItem.id)
                : [...selectedLoc, { id: categoryItem.id, text: categoryItem.text }];
            if (!isEqual(selectedLoc, updated)) {
                dispatch(setStartLocations(updated));
            }
        } else if (currentOptionIndex === 6) {
            if (
                (searchCategories.duration && categoryItem.id !== searchCategories.duration.id) ||
                !searchCategories.duration
            ) {
                dispatch(setLengthOfTrip(categoryItem));
                setLengthOfTripState(categoryItem.text);
            } else {
                dispatch(setLengthOfTrip(null));
                setLengthOfTripState(null);
            }
        }
        setCurrentOptionIndex(-1);
    };

    const getCategoryData = (constant: string): IOption[] => {
        return categoryMap[constant] || [];
    };

    const getSelectedCategoryName = (constant: string): string | undefined => {
        if (constant === ACTIVITIES) {
            return searchCategories.activities?.map((act) => act.text).join(", ");
        } else if (constant === ACCOMMODATION) {
            return searchCategories.accommodation?.map((accom) => accom.text).join(", ");
        } else if (constant === SUPPLIERS) {
            return searchCategories.suppliers?.map((sup) => sup.text).join(", ");
        } else if (constant === TRANSPORTS) {
            return searchCategories.transports?.map((trans) => trans.text).join(", ");
        } else if (constant === DESTINATIONS) {
            return searchCategories.destinations?.map((d) => d.text).join(", ");
        } else if (constant === START_LOCATIONS) {
            return searchCategories.startLocations?.map((l) => l.text).join(", ");
        } else if (constant === LENGTH_OF_TRIP) {
            return searchCategories.duration?.text;
        }
        return undefined;
    };

    const hasAnyFilterSelected = () => {
        const hasName = !!searchCategories.name?.trim();
        const hasAccommodation = (searchCategories.accommodation?.length ?? 0) > 0;
        const hasActivities = (searchCategories.activities?.length ?? 0) > 0;
        const hasSuppliers = (searchCategories.suppliers?.length ?? 0) > 0;
        const hasTransports = (searchCategories.transports?.length ?? 0) > 0;
        const hasDuration = !!searchCategories.duration;
        const hasCountry = !!searchCategories.countries?.id;
        const hasState = !!searchCategories.states?.id;
        const hasRegion = !!searchCategories.regions?.id;
        const hasLocality = !!searchCategories.localities?.id;
        const hasDestination = (searchCategories.destinations?.length ?? 0) > 0;
        const hasStartLocation = (searchCategories.startLocations?.length ?? 0) > 0;

        return hasName || hasAccommodation || hasActivities || hasSuppliers || 
               hasTransports || hasDuration || hasCountry || hasState || hasRegion || hasLocality ||
               hasDestination || hasStartLocation;
    };

    const canSearch = hasAnyFilterSelected();

    const handleSearch = () => {
        if (!canSearch) return;
        dispatch(setDay(Number(lengthOfTrip)));
        if (props.onApplyFilter) {
            props.onApplyFilter();
        }
        dispatch(setCurrentPage(1));
        if (props.onToggleShowFilter) props.onToggleShowFilter();
    };

    const handleResetSearchResult = () => {
        dispatch(setName(""));
        dispatch(resetSearch());
        setLengthOfTripState(null);
        setCurrentOptionIndex(-1);
        if (props.onResetSearch) props.onResetSearch();
    };

    const renderFilterItem = (index: number) => {
        const constant = filterConstantsArr[index];
        const selectedName = getSelectedCategoryName(constant);
        return (
            <div className="filterItem filterCol-338" key={index}>
                <SearchFilterItemComponent
                    isShowListOption={currentOptionIndex === index}
                    tagName={filterTagModel[index].title}
                    icon={filterTagModel[index].icon}
                    categoryData={getCategoryData(constant)}
                    onClickTag={() => handleClickTag(index)}
                    onCloseOptions={handleCloseOptions}
                    onClickItem={handleClickItem}
                    selectedCategoryName={selectedName}
                    name={filterTagModel[index].name}
                />
            </div>
        );
    };

    const isDesktopSearchLayout = isSearchResultPage && width > 767;

    return (
        <StyledFilterDesktop $isSearchResultPage={isSearchResultPage} width={width}>
            <div className="heading mobile">
                <span className="text">Filter</span>
                <button className="closeBtn" onClick={props.onToggleShowFilter}>
                    <img src="/images/fi_x.png" alt="" />
                </button>
            </div>
            {isDesktopSearchLayout ? (
                <div className="filterContainer">
                    <div className="filterGrid">
                        <div className="filterGridRow">
                            {renderFilterItem(0)}
                            {renderFilterItem(1)}
                            {renderFilterItem(3)}
                        </div>
                        <div className="filterGridRow">
                            {renderFilterItem(2)}
                            {renderFilterItem(4)}
                            {renderFilterItem(5)}
                        </div>
                        <div className="filterGridRow">
                            {renderFilterItem(6)}
                        </div>
                        <div className="filterGridActions">
                            <StyledButton
                                color={theme.gradients.button}
                                onClick={handleSearch}
                                disabled={!canSearch}
                                style={{ opacity: canSearch ? 1 : 0.5, cursor: canSearch ? 'pointer' : 'not-allowed' }}
                            >
                                Apply Filter
                            </StyledButton>
                            <FilterResetLink onClick={handleResetSearchResult}>
                                Reset
                            </FilterResetLink>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="searchResultPage">
                        <div className="heading">
                            <span className="text">Filter</span>
                            <button className="resetBtn" onClick={handleResetSearchResult}>
                                Reset
                            </button>
                        </div>
                    </div>
                    <div className="filterContainer">
                        <div className="filterRow">
                            {filterConstantsArr.map((_constant: string, index: number) => (
                                <React.Fragment key={index}>
                                    {renderFilterItem(index)}
                                </React.Fragment>
                            ))}
                        </div>
                        <div className="buttonRow">
                            <ResetButtonContainer
                                className={`filterItem filterCol-258 ${isSearchResultPage && "searchResult"}`}
                            >
                                {!isSearchResultPage && (
                                    <ResetButton onClick={handleResetSearchResult}>Reset</ResetButton>
                                )}
                            </ResetButtonContainer>
                            <SearchButtonContainer
                                className={`filterItem filterCol-258 ${isSearchResultPage && "searchResult"}`}
                            >
                                <SearchButton
                                    onClick={() => {
                                        if (canSearch) navigate(PATH_SEARCH_RESULT);
                                    }}
                                    disabled={!canSearch}
                                    style={{ opacity: canSearch ? 1 : 0.5, cursor: canSearch ? 'pointer' : 'not-allowed' }}
                                >
                                    Search
                                </SearchButton>
                            </SearchButtonContainer>
                            <div
                                className={`filterItem filterCol-258 ${isSearchResultPage ? "" : "applyFilterBtn"}`}
                            >
                                <StyledButton
                                    color={theme.gradients.button}
                                    onClick={handleSearch}
                                    disabled={!canSearch}
                                    style={{ opacity: canSearch ? 1 : 0.5, cursor: canSearch ? 'pointer' : 'not-allowed' }}
                                >
                                    Apply Filter
                                </StyledButton>
                            </div>
                            <ResetButtonMobile onClick={handleResetSearchResult}>
                                <span className="text">Reset</span>
                            </ResetButtonMobile>
                        </div>
                    </div>
                </>
            )}
        </StyledFilterDesktop>
    );
};

const ResetButton = styled(StyledButton)`
    margin-top: 0;
    background-color: ${(p) => p.theme.colors.gray_3};
    height: 100%;
    border-radius: none;
    color: #fff;
    @media (max-width: ${(p) => p.theme.breakPoints.breakTablet}) {
        display: none;
    }
`;

const ResetButtonContainer = styled.div`
    ${breakpoint.breakMobile`
        display: none;
    `}
`;

const SearchButtonContainer = styled.div`
    ${breakpoint.breakMobile`
        display: none;
    `}
`;

const SearchButton = styled(StyledButton)`
    margin-top: 0;
    height: 100%;
    background: linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]});
    @media (max-width: ${(p) => p.theme.breakPoints.breakTablet}) {
        display: none !important;
    }
`;

const ResetButtonMobile = styled(StyledButton)`
    display: flex;
    justify-content: center;
    align-items: center;
    .text {
        font-size: 16px;
        font-weight: 700;
        line-height: 26px;
    }
    ${breakpoint.breakTablet`
        display: none !important;
    `}
`;

const FilterResetLink = styled.button`
    font-size: 14px;
    font-weight: 700;
    color: ${(p) => p.theme.colors.red};
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    &:hover {
        text-decoration: underline;
    }
`;
